/**
 * Standalone Hotels Portal - Login Page
 * 
 * Direct login to Hotels dashboard - NO industry selector.
 * Full bilingual support (EN/FR).
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import LanguageSelector from '../../components/LanguageSelector';
import { toast } from 'sonner';
import brandConfig from '../../config/brand';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${API_BASE}/api/hotels-standalone`;

export default function StandaloneLogin() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Force dark mode for standalone pages
  useEffect(() => {
    document.documentElement.classList.add('dark');
    return () => {
      const savedTheme = localStorage.getItem('yul_theme_preference');
      if (savedTheme !== 'dark') {
        document.documentElement.classList.remove('dark');
      }
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast.error(t('auth.requiredFields'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || t('auth.loginError'));
      }

      // Store token and user
      localStorage.setItem('hotels_token', data.access_token);
      localStorage.setItem('hotels_user', JSON.stringify(data.user));

      toast.success(t('standalone.dashboard.welcomeBack') + '!');
      navigate('/standalone/dashboard');
    } catch (error) {
      toast.error(error.message || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(222,47%,11%)] flex items-center justify-center p-4">
      {/* Language Selector */}
      <div className="absolute top-4 right-4">
        <LanguageSelector variant="compact" />
      </div>

      <Card className="w-full max-w-md bg-[hsl(217,19%,12%)] border-[hsl(217,19%,27%)]">
        <CardHeader className="text-center pt-8 pb-2">
          <img 
            src="/tapelligence-logo.png" 
            alt="Tapelligence" 
            className="mx-auto h-[80px] object-contain"
            data-testid="login-logo"
          />
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">{t('standalone.login.email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t('standalone.login.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white"
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-white">{t('standalone.login.password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="bg-[hsl(217,19%,17%)] border-[hsl(217,19%,27%)] text-white pr-10"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(215,20%,65%)]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full text-white font-semibold"
              style={{ background: 'linear-gradient(135deg, #F9AC08 0%, #F05101 100%)' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('standalone.login.signingIn')}
                </>
              ) : (
                t('standalone.login.signIn')
              )}
            </Button>
          </form>
          
          {/* Demo credentials hint */}
          <div className="mt-6 p-4 rounded-lg bg-[hsl(217,19%,17%)] border border-[hsl(217,19%,27%)]">
            <p className="text-xs text-[hsl(215,20%,65%)] font-medium mb-2">{t('standalone.login.demoCredentials')}:</p>
            <p className="text-xs text-[hsl(215,20%,65%)]">
              {t('standalone.login.superAdmin')}: <span className="text-orange-400">admin@tapelligence.com</span>
            </p>
          </div>
          <p className="text-center text-[10px] text-[hsl(215,20%,40%)] mt-4" data-testid="build-version">Build: 2026-03-01-v3</p>
        </CardContent>
      </Card>
    </div>
  );
}
